import type { FieldDefinition, IntegrationProviderConfig } from '../../types';
import SlackConnectionCard from './SlackConnectionCard';
import SlackIcon from './SlackIcon';

const pullObjectTypes = [
  { label: 'Messages', value: 'channel_history' },
  { label: 'Channels', value: 'channels' },
  { label: 'Users', value: 'users' },
  { label: 'Files', value: 'files' },
];

const channelTypeOptions = [
  { label: 'Public channels', value: 'public_channel' },
  { label: 'Private channels', value: 'private_channel' },
];

function getPullFields(objectType: string): FieldDefinition[] {
  if (objectType === 'channels') {
    return [
      {
        name: 'limit',
        label: 'Limit',
        type: 'number',
        defaultValue: 50,
        placeholder: 'Max 1000',
        validation: { maxLength: 4 },
      },
      {
        name: 'excludeArchived',
        label: 'Exclude archived',
        type: 'toggle',
        defaultValue: true,
      },
      {
        name: 'types',
        label: 'Channel types',
        type: 'select',
        options: channelTypeOptions,
        placeholder: 'All (if empty)',
      },
    ];
  }

  if (objectType === 'channel_history') {
    return [
      {
        name: 'channel',
        label: 'Channel',
        type: 'select',
        options: 'dynamic',
        placeholder: 'Select channel',
        validation: { required: true },
      },
      {
        name: 'limit',
        label: 'Limit',
        type: 'number',
        defaultValue: 20,
        placeholder: 'Default 20, Max 200',
        validation: { maxLength: 3 },
      },
      {
        name: 'oldest',
        label: 'Date from (Unix ts)',
        type: 'text',
        placeholder: 'Messages after this timestamp',
      },
      {
        name: 'latest',
        label: 'Date to (Unix ts)',
        type: 'text',
        placeholder: 'Messages before this timestamp',
      },
      {
        name: 'threadOnly',
        label: 'Thread only',
        type: 'toggle',
        defaultValue: false,
        placeholder: 'If ON, only pull thread replies for a given message ts',
      },
      {
        name: 'thread_ts',
        label: 'Thread TS (parent message)',
        type: 'text',
        placeholder: 'Timestamp of parent message (when Thread only = ON)',
        dependsOn: { field: 'threadOnly', value: 'true' },
      },
    ];
  }

  if (objectType === 'users') {
    return [
      {
        name: 'limit',
        label: 'Limit',
        type: 'number',
        defaultValue: 50,
        placeholder: 'Max 999',
        validation: { maxLength: 3 },
      },
      {
        name: 'includeLocale',
        label: 'Include locale',
        type: 'toggle',
        defaultValue: false,
      },
    ];
  }

  if (objectType === 'files') {
    return [
      {
        name: 'channel',
        label: 'Channel (optional)',
        type: 'select',
        options: 'dynamic',
        placeholder: 'Filter by channel',
      },
      {
        name: 'limit',
        label: 'Limit',
        type: 'number',
        defaultValue: 20,
        placeholder: 'Default 20, Max 200',
        validation: { maxLength: 3 },
      },
    ];
  }

  return [];
}

const pushActions = [
  { label: 'Post message', value: 'post_message' },
  { label: 'Update message', value: 'update_message' },
  { label: 'Delete message', value: 'delete_message' },
  { label: 'Upload file', value: 'upload_file' },
  { label: 'Set topic', value: 'set_topic' },
  { label: 'Add reaction', value: 'add_reaction' },
  { label: 'Schedule message', value: 'schedule_message' },
];

function getPushFields(action: string): FieldDefinition[] {
  if (action === 'post_message') {
    return [
      {
        name: 'channel',
        label: 'Channel',
        type: 'select',
        options: 'dynamic',
        placeholder: 'Select channel',
        validation: { required: true },
      },
      {
        name: 'text',
        label: 'Message text',
        type: 'textarea',
        placeholder: 'Plain text (or use Data input). Optional if Block Kit JSON is set.',
        hasConnectionPoint: true,
      },
      {
        name: 'thread_ts',
        label: 'Thread TS (optional)',
        type: 'text',
        placeholder: 'Leave empty for channel message; reply in thread with message ts',
      },
      {
        name: 'richFormatting',
        label: 'Rich formatting (Block Kit)',
        type: 'toggle',
        defaultValue: false,
        placeholder: 'OFF = plain text, ON = Block Kit JSON editor',
      },
      {
        name: 'blockKitJson',
        label: 'Block Kit JSON',
        type: 'textarea',
        placeholder: 'Slack Block Kit payload (or use Data input). Optional if Message text is set.',
        dependsOn: { field: 'richFormatting', value: 'true' },
        hasConnectionPoint: true,
      },
    ];
  }

  if (action === 'update_message') {
    return [
      {
        name: 'channel',
        label: 'Channel',
        type: 'select',
        options: 'dynamic',
        placeholder: 'Select channel',
        validation: { required: true },
      },
      {
        name: 'timestamp',
        label: 'Message TS',
        type: 'text',
        placeholder: 'Timestamp of message to update',
        validation: { required: true },
      },
      {
        name: 'text',
        label: 'New text',
        type: 'textarea',
        placeholder: 'Updated message text',
        validation: { required: true },
        hasConnectionPoint: true,
      },
    ];
  }

  if (action === 'delete_message') {
    return [
      {
        name: 'channel',
        label: 'Channel',
        type: 'select',
        options: 'dynamic',
        placeholder: 'Select channel',
        validation: { required: true },
      },
      {
        name: 'timestamp',
        label: 'Message TS',
        type: 'text',
        placeholder: 'Timestamp of message to delete',
        validation: { required: true },
      },
    ];
  }

  if (action === 'upload_file') {
    return [
      {
        name: 'channel',
        label: 'Channel',
        type: 'select',
        options: 'dynamic',
        placeholder: 'Select channel',
        validation: { required: true },
      },
      {
        name: 'fileUrl',
        label: 'File URL',
        type: 'text',
        placeholder: 'File URL or path (backend may accept binary via Data input)',
        validation: { required: true },
        hasConnectionPoint: true,
      },
      {
        name: 'filename',
        label: 'Filename (optional)',
        type: 'text',
        placeholder: 'Optional display name for the file',
      },
      {
        name: 'initial_comment',
        label: 'Initial comment (optional)',
        type: 'text',
        placeholder: 'Comment to add with the file',
      },
    ];
  }

  if (action === 'set_topic') {
    return [
      {
        name: 'channel',
        label: 'Channel',
        type: 'select',
        options: 'dynamic',
        placeholder: 'Select channel',
        validation: { required: true },
      },
      {
        name: 'name',
        label: 'Topic',
        type: 'text',
        placeholder: 'New channel topic',
        validation: { required: true },
        hasConnectionPoint: true,
      },
    ];
  }

  if (action === 'add_reaction') {
    return [
      {
        name: 'channel',
        label: 'Channel',
        type: 'select',
        options: 'dynamic',
        placeholder: 'Select channel',
        validation: { required: true },
      },
      {
        name: 'timestamp',
        label: 'Message timestamp (ts)',
        type: 'text',
        placeholder: 'Message timestamp (ts)',
        validation: { required: true },
      },
      {
        name: 'reactionName',
        label: 'Reaction name',
        type: 'select',
        options: 'dynamic',
        placeholder: 'Select reaction (emoji)',
        validation: { required: true },
      },
    ];
  }

  if (action === 'schedule_message') {
    return [
      {
        name: 'channel',
        label: 'Channel',
        type: 'select',
        options: 'dynamic',
        placeholder: 'Select channel',
        validation: { required: true },
      },
      {
        name: 'post_at',
        label: 'Post at',
        type: 'datetime',
        placeholder: 'Date and time to publish',
        validation: { required: true },
        hasConnectionPoint: true,
      },
      {
        name: 'text',
        label: 'Message text',
        type: 'textarea',
        placeholder: 'Message text',
        validation: { required: true },
        hasConnectionPoint: true,
      },
    ];
  }

  return [];
}

export const slackConfig: IntegrationProviderConfig = {
  id: 'slack',
  name: 'Slack',
  icon: SlackIcon,
  brandColor: '#4A154B',
  authMethod: 'oauth2',
  ConnectionCard: SlackConnectionCard,
  pullConfig: {
    objectTypes: pullObjectTypes,
    getFields: getPullFields,
  },
  pushConfig: {
    actions: pushActions,
    getFields: getPushFields,
  },
  supportsPull: true,
  supportsPush: true,
  description:
    'Pull messages, channels, and users from Slack or post into any channel from your flows.',
  category: ['communication'],
  keywords: ['chat', 'messages', 'channels', 'team'],
  docsUrl: 'https://api.slack.com/docs',
  priority: 95,
  supportsSharing: true,
};
